import { CheckBox } from '@devotel/design-system';
import { PageSection } from '@src/components';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { useGetRegionsQuery } from '@src/services/region/regionApiSlice';
import { Badge, Button } from 'antd';
import { Filter } from 'iconsax-react';
import { useEffect, useState } from 'react';

interface CheckedItems {
  [key: string]: {
    id: string;
    checked: boolean;
  };
}

interface RegionFilterProps {
  onConfirm: (items: CheckedItems) => void;
  onCancel?: () => void;
  prevFilters?: Record<string, boolean>;
  label?: string;
  filteredCount?: number;
}

export function RegionFilter({
  label = 'Filter',
  prevFilters,
  onConfirm,
  onCancel,
  filteredCount = 0,
}: Readonly<RegionFilterProps>) {
  const [open, setOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState<CheckedItems>({});
  const { data: regions } = useGetRegionsQuery({
    includeCountries: true,
  });

  useEffect(() => {
    if (prevFilters) {
      const updated: CheckedItems = {};
      for (const key in prevFilters) {
        updated[key] = {
          id: key,
          checked: prevFilters[key],
        };
      }
      setCheckedItems(updated);
    }
  }, [prevFilters]);

  const handleToggle = (id: string, checked: boolean) => {
    setCheckedItems((prev) => ({
      ...prev,
      [id]: { id, checked },
    }));
  };

  const handleApply = () => {
    onConfirm(checkedItems);
    setOpen(false);
  };

  const handleCancel = () => {
    if (prevFilters) {
      const restored: CheckedItems = {};
      for (const key in prevFilters) {
        restored[key] = { id: key, checked: prevFilters[key] };
      }
      setCheckedItems(restored);
    }
    onCancel?.();
    setOpen(false);
  };

  const handleReset = () => {
    setCheckedItems({});
    onConfirm({});
    setOpen(false);
  };

  return (
    <div className='relative'>
      <Badge count={filteredCount} offset={[-6, 6]}>
        <Button
          variant='text'
          size='large'
          icon={
            <Filter
              variant='Linear'
              className='!text-wl-on-background'
              size='24'
            />
          }
          onClick={() => setOpen(true)}
          className='border-none shadow-none bg-transparent hover:!bg-transparent !py-0 !px-0'
        ></Button>
      </Badge>

      <ResponsiveModal
        isOpen={open}
        title={label}
        headerIcon={<Filter variant='Linear' />}
        onClose={handleCancel}
        content={
          <div className='space-y-2 md:space-y-8 mb-4'>
            <PageSection className='flex flex-col gap-4 bg-wl-background py-4 px-6'>
              {regions?.map((region) => (
                <PageSection
                  key={region.id}
                  className='bg-wl-surface cursor-pointer'
                  onClick={() => {
                    const currentChecked =
                      checkedItems[region.id]?.checked ?? false;
                    handleToggle(region.id, !currentChecked);
                  }}
                >
                  <CheckBox
                    options={[
                      {
                        value: region.id,
                        label: (
                          <div className='flex space-x-1 items-center !text-wl-on-background'>
                            <p>{region.name}</p>
                          </div>
                        ),
                      },
                    ]}
                    key={region.id}
                    value={checkedItems[region.id]?.checked ? [region.id] : []}
                    onChange={(selectedIds) => {
                      const isChecked = selectedIds.includes(region.id);
                      handleToggle(region.id, isChecked);
                    }}
                    label={
                      <div className='flex space-x-1 items-center !text-wl-on-background'>
                        <p>{region.name}</p>
                      </div>
                    }
                    className='[&_.ant-checkbox-inner]:!border-none [&_.ant-checkbox-inner]:!bg-wl-on-background [&_.ant-checkbox-inner]:opacity-20 [&_:is(.ant-checkbox-checked>.ant-checkbox-inner)]:!bg-wl-primary [&_:is(.ant-checkbox-checked>.ant-checkbox-inner)]:opacity-100'
                  />
                </PageSection>
              ))}
            </PageSection>
          </div>
        }
        footer={
          <div className='flex w-full gap-4'>
            <Button
              className='btn-secondary w-1/2 !text-wl-on-background'
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button className='btn-primary w-1/2' onClick={handleApply}>
              Apply
            </Button>
          </div>
        }
      />
    </div>
  );
}
